import React, { useCallback, useMemo } from 'react';
import {
  Edit,
  TextField,
  DateField,
  ReferenceManyField,
  Datagrid,
  useNotify,
  required,
  email,
  useGetIdentity,
  usePermissions,
  useCreate,
  maxLength,
  regex,
  Labeled,
  TabbedShowLayout,
} from 'react-admin';

import { useParams } from 'react-router-dom';

import {
  Box,
  Grid,
  Card,
  Button,
  CircularProgress,
  CardHeader,
  Divider,
  CardContent,
} from '@mui/material';

import ReferenceMemberField from '../../customFields/ReferenceMemberField';

import hasAccess from '../../utilities/hasAccess';

import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import ActivationButton from '../../customFields/ActivationButton';
import countries from '../../constants/countries';
import MemberBreadcrumb from './MemberBreadcrumb';
import Pagination from '../layout/Pagination';
import EditField from '../../customFields/EditField';
import EditSelectInput from '../../customFields/EditSelectInput';
import actions, { Actions } from '../../constants/actions';
import useValidateId from '../../hooks/useValidateId';
import resourceTypes from '../../constants/resourceTypes';
import EditReferenceArray from '../../customFields/EditReferenceArray';
import { memberCleaner } from '../../utilities/dataProvider/cleaner';
import errorNotification from '../../utilities/errorNotification';
import resourceNames from '../../constants/resourceNames';
import EditToolbar from '../layout/EditToolbar';
import AuditDatagrid from '../audit/AuditDatagrid';

const MemberEdit = () => {
  const { permissions } = usePermissions<Actions[]>();
  const { id } = useParams();
  const notify = useNotify();
  const [initiateResetPassword, { isLoading }] = useCreate(
    `members/${id}/initiate-reset-password`,
    { data: {} },
    {
      onSuccess: () => {
        notify('Password reset email sent successfully');
      },
      onError: () => {
        notify('Could not send password reset email', { type: 'error' });
      },
    },
  );

  const [initiateResetMfa, { isLoading: isResettingMfa }] = useCreate(
    `members/${id}/reset-mfa`,
    { data: {} },
    {
      onSuccess: () => {
        notify('MFA reset email sent successfully');
      },
      onError: () => {
        notify('Could not send MFA reset email', { type: 'error' });
      },
    },
  );

  const handleClick = useCallback(() => {
    initiateResetPassword();
  }, [initiateResetPassword]);

  const handleResetMfaClick = useCallback(() => {
    initiateResetMfa();
  }, [initiateResetMfa]);

  const { identity } = useGetIdentity();

  const onError = useCallback((e: unknown) => {
    notify(errorNotification(e), { type: 'error' });
  }, [notify]);

  const showEdit = useMemo(() => hasAccess(permissions, actions.MEMBER_UPDATE), [permissions]);

  const validatedId = useValidateId();
  if (!validatedId.isValid) return null;

  return (
    <Edit
      mutationMode="pessimistic"
      redirect={false}
      transform={memberCleaner}
      mutationOptions={{ onError }}
      actions={(
        <EditToolbar>
          {hasAccess(permissions, actions.MEMBER_INITIATE_RESET) && (
            <Button
              variant="contained"
              startIcon={isLoading && <CircularProgress size={14} />}
              onClick={handleClick}
              disabled={isLoading}
            >
              Reset password
            </Button>
          )}
          {hasAccess(permissions, actions.MEMBER_INITIATE_MFA_RESET) && (
            <Box display="flex" marginLeft={2}>
              <Button
                variant="contained"
                startIcon={isResettingMfa && <CircularProgress size={14} />}
                onClick={handleResetMfaClick}
                disabled={isResettingMfa}
              >
                Reset MFA
              </Button>
            </Box>
          )}
          {id !== identity?.id
            && hasAccess(permissions, actions.MEMBER_UPDATE_ACTIVATION) && (
            <Box display="flex" marginLeft={2}>
              <ActivationButton />
            </Box>
          )}
        </EditToolbar>
      )}
    >
      <MemberBreadcrumb />
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Information">
          <Grid container spacing={6}>
            <Grid size={12}>
              <Card variant="outlined">
                <CardHeader title="Member" />
                <Divider />
                <CardContent>
                  <Grid container rowSpacing={2} columnSpacing={4}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                      }}
                    >
                      <Labeled label="ID">
                        <TextField source="id" />
                      </Labeled>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                      }}
                    >
                      <EditField
                        showEdit={showEdit}
                        source="firstName"
                        label="First name"
                        validate={required()}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                      }}
                    >
                      <EditField
                        showEdit={showEdit}
                        source="lastName"
                        label="Last name"
                        validate={required()}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                      }}
                    >
                      <EditField
                        showEdit={showEdit}
                        source="email"
                        label="Email"
                        validate={[email(), required()]}
                      />
                    </Grid>
                    {hasAccess(permissions, actions.PERMISSION_ROLE_LIST) && (
                      <Grid
                        size={{
                          xs: 12,
                          md: 6,
                          lg: 4,
                        }}
                      >
                        <EditReferenceArray
                          source="permissionRoleIds"
                          showEdit={hasAccess(permissions, actions.MEMBER_UPDATE)}
                          label="Permission Roles"
                          reference={resourceNames.PERMISSION_ROLES}
                        />
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={12}>
              <Card variant="outlined">
                <CardHeader title="Additional information" />
                <Divider />
                <CardContent>
                  <Grid container rowSpacing={2} columnSpacing={4}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                      }}
                    >
                      <EditField
                        showEdit={showEdit}
                        source="occupation"
                        label="Occupation"
                        validate={[maxLength(255)]}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                      }}
                    >
                      <EditField
                        showEdit={showEdit}
                        source="birthDate"
                        dateOptions={{ timeZone: 'UTC' }}
                        label="Birth date"
                        type="date"
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                      }}
                    >
                      <EditField
                        showEdit={showEdit}
                        source="swedishSocialSecurityNumber"
                        label="Swedish social security number"
                        validate={[regex(/^[0-9]{12}$/, 'yyyymmddxxxx')]}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                      }}
                    >
                      <EditSelectInput
                        choices={countries}
                        showEdit={showEdit}
                        autocomplete
                        label="Residence"
                        source="residence"
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                      }}
                    >
                      <EditField
                        showEdit={showEdit}
                        source="foreignId"
                        label="Foreign ID"
                        validate={[maxLength(25)]}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                      }}
                    >
                      <EditSelectInput
                        choices={countries}
                        showEdit={showEdit}
                        autocomplete
                        source="foreignIdCountry"
                        label="Foreign ID country"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabbedShowLayout.Tab>
        {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
          <TabbedShowLayout.Tab label="Audit trail">
            <Grid container>
              <Grid size={12}>
                <Card variant="outlined">
                  <ReferenceManyField
                    reference="audit-tracks"
                    target="resource.id"
                    filter={{ 'resource.type': resourceTypes.MEMBER }}
                    sort={{ field: 'date', order: 'DESC' }}
                    perPage={15}
                    pagination={<Pagination disableEmptyText />}
                  >
                    <Datagrid sx={boldDataGridStyle} bulkActionButtons={false}>
                      <TextField sortable={false} source="id" label="ID" />
                      <ReferenceMemberField label="User" />
                      <TextField sortable={false} source="description" />
                      <DateField sortable source="date" showTime />
                    </Datagrid>
                  </ReferenceManyField>
                </Card>
              </Grid>
            </Grid>
          </TabbedShowLayout.Tab>
        )}
        {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
          <TabbedShowLayout.Tab label="Activity">
            <Grid container>
              <Grid size={12}>
                <Card variant="outlined">
                  <ReferenceManyField
                    reference="audit-tracks"
                    target="authenticator.id"
                    sort={{ field: 'date', order: 'DESC' }}
                    perPage={15}
                    pagination={<Pagination disableEmptyText />}
                  >
                    <AuditDatagrid hideAuthenticatorColumns />
                  </ReferenceManyField>
                </Card>
              </Grid>
            </Grid>
          </TabbedShowLayout.Tab>
        )}
      </TabbedShowLayout>
    </Edit>
  );
};

export default MemberEdit;
